import { type Expr, ExpressionError, type RefScope } from "./ast";
import { parseExpression } from "./parser";

export type Value = number | string | boolean | null | string[] | Record<string, unknown> | unknown[];

export interface ExpressionScope {
  answers: Record<string, unknown>;
  vars: Record<string, unknown>;
  hidden: Record<string, unknown>;
}

export function toNumber(v: unknown): number {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (typeof v === "string") {
    const s = v.trim().replace(/\s/g, "");
    if (s === "") return 0;
    // Deutsche Schreibweise „1.234,5“ und englische „1,234.5“ / „1234.5“ akzeptieren.
    const normalized = /,\d{1,}$/.test(s) && !/\.\d+$/.test(s) ? s.replace(/\./g, "").replace(",", ".") : s.replace(/,/g, "");
    const n = Number(normalized);
    return Number.isFinite(n) ? n : 0;
  }
  if (Array.isArray(v)) return v.length;
  return 0;
}

export function toText(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (Array.isArray(v)) return v.map(toText).join(", ");
  if (typeof v === "object") return "";
  return String(v);
}

export function isEmptyValue(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v).length === 0;
  return false;
}

export function truthy(v: unknown): boolean {
  if (typeof v === "number") return v !== 0 && !Number.isNaN(v);
  if (typeof v === "boolean") return v;
  return !isEmptyValue(v);
}

function looseEquals(a: unknown, b: unknown): boolean {
  if (a === null || a === undefined) return b === null || b === undefined || b === "";
  if (b === null || b === undefined) return a === "";
  if (typeof a === "number" || typeof b === "number") return toNumber(a) === toNumber(b);
  if (typeof a === "boolean" || typeof b === "boolean") return truthy(a) === truthy(b);
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x) => b.includes(x));
  }
  return toText(a) === toText(b);
}

const round = (n: number, digits = 0) => {
  const f = 10 ** Math.max(0, Math.min(10, Math.trunc(digits)));
  return Math.round((n + Number.EPSILON) * f) / f;
};

const flatNumbers = (args: unknown[]) => args.flatMap((a) => (Array.isArray(a) ? a : [a])).map(toNumber);

/** Erlaubte Funktionen. Alle sind rein und terminieren. */
export const FUNCTIONS: Record<string, { arity: [number, number]; fn: (...args: unknown[]) => Value }> = {
  min: { arity: [1, 50], fn: (...a) => (flatNumbers(a).length ? Math.min(...flatNumbers(a)) : 0) },
  max: { arity: [1, 50], fn: (...a) => (flatNumbers(a).length ? Math.max(...flatNumbers(a)) : 0) },
  sum: { arity: [1, 50], fn: (...a) => flatNumbers(a).reduce((s, n) => s + n, 0) },
  avg: {
    arity: [1, 50],
    fn: (...a) => {
      const n = flatNumbers(a);
      return n.length ? n.reduce((s, x) => s + x, 0) / n.length : 0;
    },
  },
  round: { arity: [1, 2], fn: (x, d) => round(toNumber(x), d === undefined ? 0 : toNumber(d)) },
  floor: { arity: [1, 1], fn: (x) => Math.floor(toNumber(x)) },
  ceil: { arity: [1, 1], fn: (x) => Math.ceil(toNumber(x)) },
  abs: { arity: [1, 1], fn: (x) => Math.abs(toNumber(x)) },
  number: { arity: [1, 1], fn: (x) => toNumber(x) },
  text: { arity: [1, 1], fn: (x) => toText(x) },
  len: { arity: [1, 1], fn: (x) => (Array.isArray(x) ? x.length : toText(x).length) },
  count: { arity: [1, 1], fn: (x) => (Array.isArray(x) ? x.length : isEmptyValue(x) ? 0 : 1) },
  empty: { arity: [1, 1], fn: (x) => isEmptyValue(x) },
  contains: {
    arity: [2, 2],
    fn: (h, n) => (Array.isArray(h) ? h.includes(toText(n)) : toText(h).toLowerCase().includes(toText(n).toLowerCase())),
  },
  concat: { arity: [1, 50], fn: (...a) => a.map(toText).join("") },
  upper: { arity: [1, 1], fn: (x) => toText(x).toUpperCase() },
  lower: { arity: [1, 1], fn: (x) => toText(x).toLowerCase() },
  if: { arity: [3, 3], fn: (c, a, b) => (truthy(c) ? (a as Value) : (b as Value)) },
  /** Alter in Jahren aus `YYYY-MM-DD` relativ zu heute oder optionalem Stichtag. */
  age: {
    arity: [1, 2],
    fn: (birth, ref) => {
      const b = /^(\d{4})-(\d{2})-(\d{2})/.exec(toText(birth));
      if (!b) return null;
      const r = ref !== undefined ? /^(\d{4})-(\d{2})-(\d{2})/.exec(toText(ref)) : null;
      const now = r ? { y: +r[1]!, m: +r[2]!, d: +r[3]! } : (() => {
        const d = new Date();
        return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate() };
      })();
      let age = now.y - +b[1]!;
      if (now.m < +b[2]! || (now.m === +b[2]! && now.d < +b[3]!)) age--;
      return age;
    },
  },
};

export function evaluateAst(expr: Expr, scope: ExpressionScope): Value {
  switch (expr.kind) {
    case "literal":
      return expr.value;
    case "ref": {
      const bag = scope[expr.scope as RefScope];
      const v = Object.prototype.hasOwnProperty.call(bag, expr.name) ? bag[expr.name] : null;
      return (v ?? null) as Value;
    }
    case "unary": {
      const v = evaluateAst(expr.arg, scope);
      return expr.op === "-" ? -toNumber(v) : !truthy(v);
    }
    case "conditional":
      return truthy(evaluateAst(expr.test, scope)) ? evaluateAst(expr.then, scope) : evaluateAst(expr.else, scope);
    case "binary": {
      if (expr.op === "&&") {
        const l = evaluateAst(expr.left, scope);
        return truthy(l) ? truthy(evaluateAst(expr.right, scope)) : false;
      }
      if (expr.op === "||") {
        const l = evaluateAst(expr.left, scope);
        return truthy(l) ? true : truthy(evaluateAst(expr.right, scope));
      }
      const l = evaluateAst(expr.left, scope);
      const r = evaluateAst(expr.right, scope);
      switch (expr.op) {
        case "+":
          if (typeof l === "string" || typeof r === "string") {
            // Zahl + numerischer Text bleibt Addition; sonst Verkettung.
            const ln = typeof l === "string" && l.trim() !== "" && !Number.isNaN(Number(l));
            const rn = typeof r === "string" && r.trim() !== "" && !Number.isNaN(Number(r));
            if ((typeof l === "number" || ln) && (typeof r === "number" || rn)) return toNumber(l) + toNumber(r);
            return toText(l) + toText(r);
          }
          return toNumber(l) + toNumber(r);
        case "-":
          return toNumber(l) - toNumber(r);
        case "*":
          return toNumber(l) * toNumber(r);
        case "/": {
          const d = toNumber(r);
          return d === 0 ? 0 : toNumber(l) / d;
        }
        case "%": {
          const d = toNumber(r);
          return d === 0 ? 0 : toNumber(l) % d;
        }
        case "==":
          return looseEquals(l, r);
        case "!=":
          return !looseEquals(l, r);
        case "<":
        case "<=":
        case ">":
        case ">=": {
          const bothText = typeof l === "string" && typeof r === "string" && (Number.isNaN(Number(l)) || Number.isNaN(Number(r)));
          const a = bothText ? l : toNumber(l);
          const b = bothText ? r : toNumber(r);
          if (expr.op === "<") return a < b;
          if (expr.op === "<=") return a <= b;
          if (expr.op === ">") return a > b;
          return a >= b;
        }
      }
      throw new ExpressionError(`Unbekannter Operator ${expr.op}`);
    }
    case "call": {
      const def = FUNCTIONS[expr.name];
      if (!def) throw new ExpressionError(`Unbekannte Funktion „${expr.name}“`);
      const [lo, hi] = def.arity;
      if (expr.args.length < lo || expr.args.length > hi) {
        throw new ExpressionError(`${expr.name}() erwartet ${lo === hi ? lo : `${lo}–${hi}`} Argument(e)`);
      }
      // `if` wertet nur den gewählten Zweig aus.
      if (expr.name === "if") {
        return truthy(evaluateAst(expr.args[0]!, scope)) ? evaluateAst(expr.args[1]!, scope) : evaluateAst(expr.args[2]!, scope);
      }
      return def.fn(...expr.args.map((a) => evaluateAst(a, scope)));
    }
  }
}

const cache = new Map<string, Expr>();

/** Parst (mit Cache) und wertet aus. Wirft ExpressionError bei Syntax-/Funktionsfehlern. */
export function evaluateExpression(src: string, scope: ExpressionScope): Value {
  let ast = cache.get(src);
  if (!ast) {
    ast = parseExpression(src);
    if (cache.size > 1000) cache.clear();
    cache.set(src, ast);
  }
  return evaluateAst(ast, scope);
}
